import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Clock,
  Target,
  RefreshCw,
  Gem,
  Download,
  FileText,
  FileSpreadsheet,
  FileJson,
  Globe,
  Trophy,
  ChevronDown,
  CalendarDays,
  Sparkles,
} from "lucide-react";

export const ELITE_LEAGUES = [
  { slug: "champions-league", name: "Champions League", country: "Europe", flag: "🏆" },
  { slug: "europa-league",    name: "Europa League",    country: "Europe", flag: "🥈" },
  { slug: "conference-league",name: "Conference League",country: "Europe", flag: "🥉" },
  { slug: "nations-league",   name: "Nations League",   country: "Europe", flag: "🇪🇺" },
  { slug: "premier-league", name: "Premier League", country: "England",  flag: "🇬🇧" },
  { slug: "la-liga",        name: "La Liga",        country: "Spain",    flag: "🇪🇸" },
  { slug: "serie-a",        name: "Serie A",        country: "Italy",    flag: "🇮🇹" },
  { slug: "bundesliga",     name: "Bundesliga",     country: "Germany",  flag: "🇩🇪" },
  { slug: "ligue-1",        name: "Ligue 1",        country: "France",   flag: "🇫🇷" },
  { slug: "liga-portugal",  name: "Liga Portugal",  country: "Portugal", flag: "🇵🇹" },
];

export const TIERS = [
  { id: "all", name: "All", icon: "🎯", description: "Top 25 mixed picks" },
  { id: "diamond", name: "Diamond", icon: "💎", description: "Top 10 highest confidence" },
  { id: "platinum", name: "Platinum", icon: "🏆", description: "Top 15 premium picks" },
  { id: "gold", name: "Gold", icon: "🥇", description: "Top 20 strong picks" },
  { id: "silver", name: "Silver", icon: "🥈", description: "Top 25 balanced picks" },
  { id: "bronze", name: "Bronze", icon: "🥉", description: "Top 30 with riskier opportunities" },
];

/** Simple 2-option segmented control */
const Segmented = ({ options, value, onChange, testidPrefix }) => (
  <div
    className="inline-flex items-center gap-1 p-1 rounded-full border border-border bg-card"
    role="tablist"
    data-testid={`${testidPrefix}-segmented`}
  >
    {options.map((o) => {
      const active = value === o.id;
      return (
        <button
          key={o.id}
          role="tab"
          aria-selected={active}
          onClick={() => onChange(o.id)}
          data-testid={`${testidPrefix}-${o.id}-btn`}
          className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-xs font-semibold transition-colors ${
            active
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {o.icon && <o.icon className="w-3.5 h-3.5" />}
          {o.label}
          {o.badge && (
            <Badge className="ml-1 bg-primary text-primary-foreground h-4 px-1.5 text-[9px] leading-none">
              {o.badge}
            </Badge>
          )}
        </button>
      );
    })}
  </div>
);

export const HeroActionBar = ({
  viewMode,
  setViewMode,
  bigLeaguesStatus,
  bigLeaguesMeta,
  selectedDay,
  setSelectedDay,
  selectedTier,
  setSelectedTier,
  selectedEliteLeague,
  setSelectedEliteLeague,
  refreshing,
  loading,
  compiling,
  onRefresh,
  onCompile,
  onExport,
}) => {
  const activeElite = ELITE_LEAGUES.find((l) => l.slug === selectedEliteLeague);
  return (
    <div className="space-y-5 mb-10" data-testid="hero-action-bar">
      {/* Row 1 — scope + day (left) · actions (right) */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <Segmented
            testidPrefix="scope"
            value={viewMode}
            onChange={setViewMode}
            options={[
              { id: "all", label: "All leagues", icon: Globe },
              {
                id: "big-leagues",
                label: "Big Leagues Premium",
                icon: Trophy,
                badge: bigLeaguesStatus?.status === "active" ? "LIVE" : null,
              },
              { id: "elite-week", label: "Elite week", icon: CalendarDays },
            ]}
          />

          {viewMode === "all" && (
            <Segmented
              testidPrefix="day"
              value={selectedDay}
              onChange={setSelectedDay}
              options={[
                { id: "today", label: "Today", icon: Clock },
                { id: "tomorrow", label: "Tomorrow", icon: Target },
              ]}
            />
          )}

          {viewMode === "elite-week" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="elite-league-dropdown-trigger"
                  className="h-8 rounded-full px-3.5 border-border bg-card hover:bg-muted text-foreground text-xs font-semibold"
                >
                  <span className="mr-1.5" aria-hidden>
                    {activeElite?.flag || "🏆"}
                  </span>
                  {activeElite ? activeElite.name : "Pick a league"}
                  <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="rounded-xl min-w-[220px]">
                <DropdownMenuLabel className="eyebrow">Elite leagues · 7-day window</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ELITE_LEAGUES.map((lg) => (
                  <DropdownMenuItem
                    key={lg.slug}
                    onClick={() => setSelectedEliteLeague(lg.slug)}
                    data-testid={`elite-league-option-${lg.slug}`}
                    className={selectedEliteLeague === lg.slug ? "bg-muted font-semibold" : ""}
                  >
                    <span className="mr-2" aria-hidden>{lg.flag}</span>
                    {lg.name}
                    <span className="ml-auto text-[10px] text-muted-foreground">{lg.country}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {viewMode === "all" && (
          <div className="flex items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  disabled={refreshing || loading}
                  className="h-9 rounded-full px-5 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-sm shadow-primary/20"
                  data-testid="regenerate-predictions-btn"
                >
                  {refreshing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                      Regenerating
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-1.5" />
                      Regenerate
                    </>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-2xl">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Regenerate the entire prediction list?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-sm leading-relaxed">
                    This wipes the prediction cache for{" "}
                    <span className="font-semibold text-foreground">{selectedDay === "today" ? "today" : "tomorrow"}</span>{" "}
                    and recomputes every match from scratch — fresh ELO,
                    Bivariate Poisson, calibration, and Betway availability.
                    <br /><br />
                    Takes <span className="font-semibold text-foreground">15–90 seconds</span> depending on fixture count.
                    Continue?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="regenerate-cancel-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onRefresh}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="regenerate-confirm-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    Yes — regenerate all
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <Button
              onClick={onCompile}
              disabled={compiling}
              variant="outline"
              className="h-9 rounded-full px-4 border-border bg-card hover:bg-muted text-foreground"
              data-testid="compile-tiers-btn"
            >
              <Gem className={`w-4 h-4 mr-1.5 ${compiling ? "animate-pulse" : ""}`} />
              {compiling ? "Compiling" : "Compile tiers"}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="h-9 rounded-full px-4 bg-foreground text-background hover:bg-foreground/90"
                  data-testid="export-btn"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="rounded-xl" align="end">
                <DropdownMenuItem onClick={() => onExport("csv")} data-testid="export-csv">
                  <FileText className="w-4 h-4 mr-2" /> CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onExport("xlsx")} data-testid="export-xlsx">
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onExport("json")} data-testid="export-json">
                  <FileJson className="w-4 h-4 mr-2" /> JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onExport("pdf")} data-testid="export-pdf">
                  <FileText className="w-4 h-4 mr-2" /> PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Tier pills row */}
      {viewMode === "all" && (
        <div
          className="flex flex-wrap items-center gap-2"
          data-testid="tier-selector"
        >
          <span className="eyebrow mr-1">Tier</span>
          {TIERS.map((t) => {
            const active = selectedTier === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setSelectedTier(t.id)}
                data-testid={`tier-${t.id}-btn`}
                title={t.description}
                className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-xs font-semibold border transition-colors ${
                  active
                    ? "bg-foreground text-background border-foreground"
                    : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-foreground/50"
                }`}
              >
                <span aria-hidden>{t.icon}</span>
                {t.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Big Leagues banner */}
      {viewMode === "big-leagues" && bigLeaguesMeta?.european_status && (
        <div
          className={`rounded-xl border px-4 py-3 text-sm flex items-start gap-2 ${
            bigLeaguesMeta.european_status.has_european_elite
              ? "border-success/40 bg-success/10 text-foreground"
              : "border-border bg-muted text-muted-foreground"
          }`}
          data-testid="big-leagues-banner"
        >
          <Trophy className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div>{bigLeaguesMeta.european_status.message}</div>
            {typeof bigLeaguesMeta.scheduled_fixtures_in_db === "number" && (
              <div className="text-xs opacity-75 mt-0.5">
                {bigLeaguesMeta.scheduled_fixtures_in_db} scheduled ·{" "}
                {bigLeaguesMeta.predictions_built} analysed
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroActionBar;
